import { DataTable } from '@elliemae/ds-data-table';
import { LoanProgram, useLookup } from '@frontend/pricing-search';
import { arrayOf, bool, shape } from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { selectProduct } from '../../../../data/lockSummary/actions';
import { setLockedProgramData } from '../../../../data/origin/actions';
import { useLockedProgramSelector } from '../../../../data/useSelectors';
import { useAppData } from '../../../../common/utils/customHooks/useAppData';
import { ProgramDetailsModal } from '../../../LockSummary/components/ProgramDetailsModal';
import {
  deleteEligibilityException,
  ExceptionDialog,
  getEligibilityException,
  saveEligibilityException,
  showExceptionAppliedToast,
  showExceptionDeletedToast,
  showExceptionDeleteErrorToast,
  showExceptionErrorToast,
  type EligibilityException,
  type SaveAndQualifyPayload,
} from '../EligibilityException';
import { StyledDataTableWrapper } from '../../../global/styles/styled';
import { EligibleProductsToolbar } from '../ClassicGrid/EligibleProductsToolbar';
import { FILTERS_INITIAL_STATE } from '../ClassicGrid/constants';
import { getIneligibleGridColumns } from './utils/constants';
import { useIneligibleProductsGridHooks } from './utils/customHooks';

export const IneligibleProductsGrid = ({
  loanPrograms,
  loanLimits,
  refreshPrograms,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [programSlide, setProgramSlide] = useState(false);
  const [exceptionProgram, setExceptionProgram] = useState<LoanProgram | null>(
    null,
  );
  const [exceptionMode, setExceptionMode] = useState<'apply' | 'manage'>(
    'apply',
  );
  const [existingException, setExistingException] =
    useState<EligibilityException | null>(null);
  const [isLoadingException, setIsLoadingException] = useState(false);
  const actionRef = useRef<any>({});
  const [activeFilters, setActiveFilters] = useState(FILTERS_INITIAL_STATE);
  const clientsettings = useLookup('clientSettings', '');
  const { focusIneligible, programID } = useLockedProgramSelector();
  const dispatch = useDispatch();
  const { loanId } = useSelector(({ EPPS }: any) => EPPS.loanPrograms || {});
  const qualifyPrograms = useAppData('qualifyPrograms');
  const setActiveTab = useAppData('setActiveTab');
  const store = useStore();

  // Persist the exception, then re-qualify, which can promote the program out of the ineligible bucket.
  const handleSaveAndQualify = async ({
    programId,
    body,
  }: SaveAndQualifyPayload): Promise<boolean> => {
    try {
      await saveEligibilityException({ loanId, programId, body });
    } catch {
      showExceptionErrorToast();
      return false;
    }
    try {
      if (qualifyPrograms) {
        await qualifyPrograms();
      }
    } catch {
      showExceptionErrorToast();
      return true;
    }
    showExceptionAppliedToast(exceptionProgram?.program ?? '');
    // If the exception promoted this program to eligible, jump to the Standard tab.
    const programs =
      (store.getState() as any)?.EPPS?.loanPrograms?.loanPrograms ?? [];
    const promoted = programs.some(
      (program: any) =>
        program?.programID === programId && program?.deleted === 0,
    );
    if (promoted && setActiveTab) {
      setActiveTab('Classic');
    }
    return true;
  };

  // Delete the exception then re-qualify. Keep the dialog open only if the
  // delete itself fails; a failed re-qualify still closes (delete persisted).
  const handleDelete = async (): Promise<boolean> => {
    const programId = (exceptionProgram as any)?.programID;
    try {
      await deleteEligibilityException({ loanId, programId });
    } catch {
      showExceptionDeleteErrorToast();
      return false;
    }
    try {
      if (qualifyPrograms) {
        await qualifyPrograms();
      }
    } catch {
      showExceptionDeleteErrorToast();
      return true;
    }
    showExceptionDeletedToast(exceptionProgram?.program ?? '');
    return true;
  };

  // Manage mode: load the saved exception to populate the dialog.
  useEffect(() => {
    const programId = (exceptionProgram as any)?.programID;
    if (exceptionMode !== 'manage' || !loanId || !programId) {
      return undefined;
    }
    let cancelled = false;
    setIsLoadingException(true);
    getEligibilityException({ loanId, programId })
      .then((result) => {
        if (!cancelled) {
          setExistingException(result);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingException(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [exceptionMode, exceptionProgram, loanId]);

  const handleFilterChange = (filterName, value) => {
    setActiveFilters((prevState) => ({ ...prevState, [filterName]: value }));
  };

  const { newRows } = useIneligibleProductsGridHooks(
    loanPrograms,
    loanLimits,
    activeFilters,
  ) as any;

  useEffect(() => {
    if (focusIneligible && newRows?.length !== 0) {
      if (actionRef?.current?.scrollToIndex) {
        const programIndex = newRows.findIndex(
          (elem) => elem?.programID === programID,
        );
        actionRef.current.scrollToIndex(programIndex, { align: 'center' });
        setExpandedRows((prevExpandedRows) => {
          const newExpanded = { ...prevExpandedRows };
          newExpanded[programIndex] = true;
          return newExpanded;
        });
        dispatch(setLockedProgramData({ focusIneligible: false }));
      }
    }
  }, [focusIneligible, actionRef, newRows]);

  if (clientsettings !== '') {
    return (
      <div style={{ height: 'calc(100% - 100px)' }}>
        <EligibleProductsToolbar
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          refreshPrograms={refreshPrograms}
          showTarget={false}
          showFlagged={false}
        />
        <ProgramDetailsModal
          isSlide
          isOpen={programSlide}
          toggleModalSlide={() => setProgramSlide(false)}
        />
        <ExceptionDialog
          isOpen={Boolean(exceptionProgram)}
          mode={exceptionMode}
          program={exceptionProgram}
          existingException={existingException ?? undefined}
          isLoading={isLoadingException}
          onClose={() => {
            setExceptionProgram(null);
            setExistingException(null);
          }}
          onDelete={handleDelete}
          onSaveAndQualify={handleSaveAndQualify}
        />
        <StyledDataTableWrapper>
          <DataTable
            uniqueRowAccessor="programID"
            actionRef={actionRef}
            height="100%"
            paginated
            isExpandable
            data={newRows}
            expandedRows={expandedRows}
            onRowExpand={setExpandedRows}
            columns={getIneligibleGridColumns()}
            cellRendererProps={{
              onClickProgramName: (rowData) => {
                dispatch(selectProduct(rowData));
                setProgramSlide(true);
              },
              onApplyException: (rowData) => {
                setExistingException(null);
                setExceptionMode('apply');
                setExceptionProgram(rowData);
              },
              onManageException: (rowData) => {
                setExistingException(null);
                setExceptionMode('manage');
                setExceptionProgram(rowData);
              },
            }}
            colsLayoutStyle="auto"
            noResultsMessage="No Results Found"
            noResultsSecondaryMessage="You do not have any Programs at this time."
            rowKey="programID"
          />
        </StyledDataTableWrapper>
      </div>
    );
  }
  return null;
};

IneligibleProductsGrid.defaultProps = {
  loanPrograms: [],
  loanLimits: [],
  refreshPrograms: false,
};

IneligibleProductsGrid.propTypes = {
  loanPrograms: arrayOf(shape({})),
  loanLimits: arrayOf(shape({})),
  refreshPrograms: bool,
};
